import { Injectable } from '@angular/core';
import { DatePipe } from '@angular/common';
import { TranslateService } from '@ngx-translate/core';
import { Decimal } from 'decimal.js';

import { CurrencyScale, FunctionalAnalysisObjectTypeEnum } from '../models/enums';
import { SortConstants, TranslationConstants, LanguageConstants } from '../constants';
import { Transaction, BaseOption, LanguageIsoInfo } from '../models';
import { BaseTransaction } from '../models/base-transaction';
import { Collapsible } from '../interfaces';

@Injectable()
export class UtilsService {

  constructor(
    private _datePipe: DatePipe,
    private _translate: TranslateService
  ) {}

  public collapseAll(objectsToCollapse: Collapsible[]): void {
    if (objectsToCollapse && objectsToCollapse.length) {
      objectsToCollapse.map(obj => {
        obj.collapsed = true;
      });
    }
  }

  public getPaginationQueryString(length, page): string {
    const queryParameters = [];
    let queryString: string;
    if (!!length) {
      queryParameters.push(`length=${length}`);
    }
    if (!!page) {
      queryParameters.push(`page=${page}`);
    }
    if (queryParameters.length > 0) {
      queryString = `${queryParameters.join('&')}`;
    }
    return queryString;
  }

  public getSortQueryString(sort: string, asc = true): string {
    const order = asc ? SortConstants.ASC : SortConstants.DESC;
    return `sort=${sort}&order=${order}`;
  }

  public getCurrencyScaleString(currencyScale: number, hasDash = true): string {
    if (!CurrencyScale[currencyScale]) {
      return '';
    }

    // we don't want to display currency scale if full value is set
    if (currencyScale === CurrencyScale.FullValue) {
      return hasDash ? '' : this._translate.instant(this.getCurrencyScaleTranslationKey(currencyScale));
    }
    const scaleString = this._translate.instant(this.getCurrencyScaleTranslationKey(currencyScale));

    if (!hasDash && !!scaleString) {
      return scaleString;
    }

    return !!scaleString ? ` - ${scaleString}` : '';
  }

  public getCurrencyScaleTranslationKey(currencyScale: number): string {
    switch (currencyScale) {
      case CurrencyScale.FullValue:
        return TranslationConstants.CURRENCY_SCALE_FULL_VALUE;
      case CurrencyScale.Hundredths:
        return TranslationConstants.CURRENCY_SCALE_HUNDREDTHS;
      case CurrencyScale.Thousandths:
        return TranslationConstants.CURRENCY_SCALE_THOUSANDTHS;
      case CurrencyScale.TenThousands:
        return TranslationConstants.CURRENCY_SCALE_TEN_THOUSANDS;
      case CurrencyScale.HundredThousands:
        return TranslationConstants.CURRENCY_SCALE_HUNDRED_THOUSANDS;
      case CurrencyScale.Millionths:
        return TranslationConstants.CURRENCY_SCALE_MILLIONTHS;
      case CurrencyScale.TenMillionths:
        return TranslationConstants.CURRENCY_SCALE_TEN_MILLIONTHS;
      case CurrencyScale.HundredMillionths:
        return TranslationConstants.CURRENCY_SCALE_HUNDRED_MILLIONTHS;
      default:
        return '';
    }
  }

  public getCurrencyScale(currencyScale: CurrencyScale): number {
    switch (currencyScale) {
      case CurrencyScale.FullValue:
        return 1;
      case CurrencyScale.Hundredths:
        return 100;
      case CurrencyScale.Thousandths:
        return 1000;
      case CurrencyScale.TenThousands:
        return 10000;
      case CurrencyScale.HundredThousands:
        return 100000;
      case CurrencyScale.Millionths:
        return 1000000;
      case CurrencyScale.TenMillionths:
        return 10000000;
      case CurrencyScale.HundredMillionths:
        return 100000000;
      default:
        return;
    }
  }

  public getEntityCodeFromTransaction(transaction: Transaction, isSource = true): string {
    if (!transaction.legalEntityTransactions || !transaction.legalEntityTransactions.find(legalEntityTransaction => legalEntityTransaction.isSource === isSource) || !transaction.legalEntityTransactions.find(legalEntityTransaction => legalEntityTransaction.isSource === isSource).entity) {
      return '';
    } else {
      return transaction.legalEntityTransactions.find(legalEntityTransaction => legalEntityTransaction.isSource === isSource).entity.code;
    }
  }

  public getFormatedTransactionDate(transaction: BaseTransaction): string {
    if (!transaction.transferDateYear) {
      return '';
    }

    const monthYearFormat = transaction.transferDateMonth ? `MMM, y` : `y`;
    const format = transaction.transferDateDay ? 'mediumDate' : monthYearFormat;

    return this._datePipe.transform(transaction.transferDate, format);
  }

  public setDatesForTransactions(transactions: BaseTransaction[]): void {
    if (transactions) {
      transactions.map(transaction => {
        transaction.transferDate = this.createDate(transaction);
      });
    }
  }

  public getFunctionalAnalysisIdentifierTranslationKey(type): string {
    switch (type) {
      case FunctionalAnalysisObjectTypeEnum.Pba:
        return 'pba.name';
      case FunctionalAnalysisObjectTypeEnum.Tba:
        return 'tba.tba_name';
      case FunctionalAnalysisObjectTypeEnum.UncontrolledTransaction:
        return 'transaction.transaction_id';
      default:
        return '';
    }
  }

  public createDate(transaction): string {
    const [day, month, year] = [
      transaction.transferDateDay,
      transaction.transferDateMonth,
      transaction.transferDateYear,
    ];

    // Since Angular' date pipe won't handle partial dates
    // we add 1 for valid date but it's not shown
    const monthYearDate = month ? `${month}-1-${year}` : `1-1-${year}`;
    return day ? `${month}-${day}-${year}` : monthYearDate;
  }

  public expandAll(objectsToExpand: Collapsible[]): void {
    objectsToExpand.forEach(obj => {
      obj.collapsed = false;
    });
  }

  // convert enum into the array of dropdown options, so it can be used in dropdown
  public enumToArray(enumValues: object): BaseOption[] {
    return Object.keys(enumValues)
      .filter(key => isNaN(Number(enumValues[key])))
      .map(key => ({ id: +key, name: enumValues[key] }));
  }

  // Calculate from MNE value to local currency scale
  public calcToLocalCurrency(
    inputValue: number,
    mneCurrencyScale: CurrencyScale,
    conversionValue: number,
    localCurrencyScale: CurrencyScale
  ): number {
    const localScale = new Decimal(this.getCurrencyScale(localCurrencyScale));
    const mneScale = new Decimal(this.getCurrencyScale(mneCurrencyScale));
    const input = new Decimal(inputValue);
    const conversion = new Decimal(conversionValue);

    return input.times(mneScale).dividedBy(localScale.times(conversion)).toNumber();
  }

  // Calculate from MNE value to local currency scale
  public calcToMneCurrency(
    inputValue: number,
    mneCurrencyScale: CurrencyScale,
    conversionValue: number,
    localCurrencyScale: CurrencyScale
  ): number {
    const localScale = new Decimal(this.getCurrencyScale(localCurrencyScale));
    const mneScale = new Decimal(this.getCurrencyScale(mneCurrencyScale));
    const input = new Decimal(inputValue);
    const conversion = new Decimal(conversionValue);

    return input.times(localScale.times(conversion)).dividedBy(mneScale).toNumber();
  }

  public jsonClone(object: any): any {
    return JSON.parse(JSON.stringify(object));
  }

  public getFileExtensionFromFilename(filename: string): string {
    const fileExtensionMatchArray = filename.match(/\..+/);
    const fileExtension = (fileExtensionMatchArray && fileExtensionMatchArray[0]) || '';

    return fileExtension;
  }

  public getFilenameWithoutFileExtension(filename: string): string {
    const filenameMatchArray = filename.match(/([^\.]+)\./);
    const matchedFilename = (filenameMatchArray && filenameMatchArray[1]) || 'untitled';

    return matchedFilename;
  }

  public doesFilenameEndWithPdf(filename: string): boolean {
    return (/\.pdf$/i).test(filename);
  }

  public findDefaultLanguage(languageList: LanguageIsoInfo[], lastLanguageIso: string): LanguageIsoInfo {
    const lastUsedLanguageIsoInfo: LanguageIsoInfo = languageList.find(language => language.language_iso === lastLanguageIso);
    const englishLanguageIsoInfo: LanguageIsoInfo = languageList.find(language => language.language_iso === LanguageConstants.BY_NAME.ENGLISH.ISO_CODE);

    const languageToUseIsoInfo: LanguageIsoInfo = lastUsedLanguageIsoInfo || englishLanguageIsoInfo || languageList[0];

    return languageToUseIsoInfo;
  }
  
  public isNumber(value: any): boolean {
    if (!value && value !== 0) {
      return false;
    }
    return !isNaN(value);
  }

}
